import { useMemo } from "react";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Tooltip } from "primereact/tooltip";
import { themeClasses } from "sj-ui-toolkit/src/atlas/utils";
import { calculatePathDuration } from "@/api/recommendedAssignments";
import type { Lesson } from "@/api/lessons";
import type { FormValues, FormSettings } from "@/api/assignments";

type ValidationError = { hasError?: boolean; message?: string };

interface ReviewValidationErrors {
  paths?: ValidationError;
  audience?: ValidationError;
  settings?: {
    name?: ValidationError;
    description?: ValidationError;
    date?: ValidationError;
  };
}

interface RecommendedFormValues {
  orderedLessonKeys: string[];
  target: FormValues["target"];
  settings: FormSettings & { description?: string };
}

interface RecommendedReviewProps {
  values: RecommendedFormValues;
  onStepChange: (step: number) => void;
  validationErrors?: ReviewValidationErrors;
  isNameUnique?: boolean;
  lessonMap?: Record<string, Lesson>;
}

const RecommendedReview = ({
  values,
  onStepChange,
  validationErrors,
  isNameUnique = true,
  lessonMap = {},
}: RecommendedReviewProps) => {
  const orderedLessonsWithDetails = useMemo(() => {
    if (!values.orderedLessonKeys.length || !Object.keys(lessonMap).length) return [];
    return values.orderedLessonKeys
      .map(key => lessonMap[key])
      .filter((l): l is Lesson => Boolean(l));
  }, [values.orderedLessonKeys, lessonMap]);

  const pathDuration = useMemo(
    () => calculatePathDuration(lessonMap, values.orderedLessonKeys),
    [lessonMap, values.orderedLessonKeys]
  );

  const selectedLessonCount = values.orderedLessonKeys.length;
  const usersCount = values.target?.users?.length ?? 0;

  const formatDate = (dateString?: string | Date | null): string => {
    if (!dateString) return "Not specified";
    return new Date(dateString).toLocaleDateString();
  };

  const settingsHasError =
    validationErrors?.settings?.name?.hasError ||
    validationErrors?.settings?.description?.hasError ||
    validationErrors?.settings?.date?.hasError ||
    !isNameUnique;

  return (
    <div className="grid grid-cols-2 gap-4" data-testid="review-container">
      {/* Left Column */}
      <div>
        {/* Path Creation */}
        <Card className={`${themeClasses.bgElevation2} min-h-44`}>
          <div className="flex items-center gap-2 mb-2">
            <h4 className="font-semibold text-lg flex-1 flex items-center gap-1">
              1. Path Creation
              {validationErrors?.paths?.hasError && (
                <span>
                  <i
                    className={`pi pi-info-circle ${themeClasses.iconError}`}
                    style={{ fontSize: "1rem" }}
                    id="review-path-error"
                    tabIndex={0}
                    aria-label="Path Creation validation error"
                  />
                  <Tooltip target="#review-path-error" position="right">
                    {validationErrors.paths.message}
                  </Tooltip>
                </span>
              )}
            </h4>
            <Button
              label="Edit"
              onClick={() => onStepChange(0)}
              link
              data-testid="review-edit-path-selection-button"
            />
          </div>
          {selectedLessonCount > 0 ? (
            <div>
              <div className="mb-3">
                <span className="font-semibold">Total Lessons: </span>
                {selectedLessonCount} {selectedLessonCount === 1 ? "lesson" : "lessons"}
              </div>
              <div>
                <span className="font-semibold">Estimated Duration: </span>
                {pathDuration}
              </div>
            </div>
          ) : (
            <div>No lessons selected, please edit this section of your Assignment.</div>
          )}
        </Card>

        {/* Path Order */}
        <Card className={`${themeClasses.bgElevation2} mt-4 h-96`}>
          <div className="flex items-center gap-2 mb-2">
            <h4 className="font-semibold text-lg flex-1">2. Path Order</h4>
            <Button
              label="Edit"
              onClick={() => onStepChange(1)}
              link
              data-testid="review-edit-path-order-button"
            />
          </div>
          {orderedLessonsWithDetails.length > 0 ? (
            <ul className="max-h-72 overflow-auto">
              {orderedLessonsWithDetails.map((lesson, index) => (
                <li key={lesson.lessonKey ?? lesson.lesson_key ?? index}>
                  {index + 1}. {lesson.title ?? lesson.name}
                </li>
              ))}
            </ul>
          ) : (
            <div>No lessons ordered yet</div>
          )}
        </Card>
      </div>

      {/* Right Column */}
      <div>
        {/* Audience */}
        <Card className={`${themeClasses.bgElevation2} min-h-44`}>
          <div className="flex items-center gap-2 mb-2">
            <h4 className="font-semibold text-lg flex-1 flex items-center gap-1">
              3. Audience
              {validationErrors?.audience?.hasError && (
                <span>
                  <i
                    className={`pi pi-info-circle ${themeClasses.iconError}`}
                    style={{ fontSize: "1rem" }}
                    id="review-audience-error"
                    tabIndex={0}
                    aria-label="Audience validation error"
                  />
                  <Tooltip target="#review-audience-error" position="right">
                    {validationErrors.audience.message}
                  </Tooltip>
                </span>
              )}
            </h4>
            <Button
              label="Edit"
              onClick={() => onStepChange(2)}
              link
              data-testid="review-edit-audience-button"
            />
          </div>
          {usersCount > 0 ? (
            <div>
              <span className="font-semibold">
                Selected {usersCount === 1 ? "User" : "Users"}:{" "}
              </span>
              {usersCount} {usersCount === 1 ? "user" : "users"} selected
            </div>
          ) : (
            <div>No users selected, please edit this section of your Assignment.</div>
          )}
        </Card>

        {/* Settings */}
        <Card className={`${themeClasses.bgElevation2} mt-4 h-96`}>
          <div className="flex items-center gap-2 mb-2">
            <h4 className="font-semibold text-lg flex-1 flex items-center gap-1">
              4. Assignment Settings
              {settingsHasError && (
                <span>
                  <i
                    className={`pi pi-info-circle ${themeClasses.iconError}`}
                    style={{ fontSize: "1rem" }}
                    id="review-settings-error"
                    tabIndex={0}
                    aria-label="Assignment Settings validation error"
                  />
                  <Tooltip target="#review-settings-error" position="right">
                    <ul className="list-disc pl-4">
                      {validationErrors?.settings?.name?.hasError && (
                        <li>{validationErrors.settings.name.message}</li>
                      )}
                      {validationErrors?.settings?.description?.hasError && (
                        <li>{validationErrors.settings.description.message}</li>
                      )}
                      {validationErrors?.settings?.date?.hasError && (
                        <li>{validationErrors.settings.date.message}</li>
                      )}
                      {!isNameUnique && <li>Assignment name must be unique</li>}
                    </ul>
                  </Tooltip>
                </span>
              )}
            </h4>
            <Button
              label="Edit"
              onClick={() => onStepChange(3)}
              link
              data-testid="review-edit-settings-button"
            />
          </div>
          {values.settings?.name || values.settings?.endDate || values.settings?.description ? (
            <>
              {values.settings.name && (
                <div className="mb-3">
                  <span className="font-semibold">Assignment Name: </span>
                  {values.settings.name}
                </div>
              )}
              {values.settings.endDate && (
                <div className="mb-3">
                  <span className="font-semibold">End Date: </span>
                  {formatDate(values.settings.endDate)}
                </div>
              )}
              {values.settings.description && (
                <div>
                  <span className="font-semibold">Description: </span>
                  <div>{values.settings.description}</div>
                </div>
              )}
            </>
          ) : (
            <div>No settings configured, please edit this section of your Assignment.</div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default RecommendedReview;
